import React, { useState, useEffect } from "react";
import { Button } from "../../@/components/ui/button";
import { FaRegFilePdf, FaDownload } from "react-icons/fa6";
import { FaBarcode, FaCalendarAlt, FaShippingFast } from "react-icons/fa";
import { MdLabel } from "react-icons/md";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { cn, useDecodedTokenResult } from "../../lib/utils";
import ScheduleDeliverySection from "./ScheduleDeliverySection";
import { useGetClickShipRatesForVendorMutation } from "../../api/api";
import BookShipmentSection from "./BookShipmentSection";
import { BiSolidLabel } from "react-icons/bi";

interface LabelFormat {
  size: string;
  format: string;
  url: string;
  padded?: boolean;
}

const OrderLabeldata = ({
  shippingLabels,
  data,
  orderApi,
}: {
  shippingLabels: string;
  data: any;
  orderApi: any;
}) => {
  const [labelData, setLabelData] = useState<string>(shippingLabels);
  const [isShippingLabelExpired, setIsShippingLabelExpired] = useState(false);
  const [parsedLabel, setParsedLabel] = useState<LabelFormat[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showScheduleDelivery, setShowScheduleDelivery] = useState(false);
  const [showBookShippmentList, setShowBookShippmentList] = useState(false);
  const [getClickShipRates, { isLoading }] =
    useGetClickShipRatesForVendorMutation();
  const decodedToken = useDecodedTokenResult();
  const role = decodedToken.role;
  useEffect(() => {
    try {
      const parsedData = JSON.parse(labelData);

      // Handle single label format
      if (parsedData.label_format) {
        const formattedLabel: LabelFormat = {
          size: parsedData.label_size,
          format: parsedData.label_format,
          url: `data:application/zpl;base64,${parsedData.label_string}`,
        };
        setParsedLabel([formattedLabel]);
      }
      // Handle array format
      else {
        setParsedLabel(parsedData);
      }
    } catch (error) {
      console.error("Error parsing label data:", error);
    }
  }, [labelData]);

  const handleOpenLabel = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!parsedLabel || parsedLabel.length === 0) {
    return null;
  }

  return (
    <div className="p-4 md:p-7 border-b">
      <div
        className="flex items-center justify-between cursor-pointer p-2 mb-4"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
          <div className="flex items-center gap-2">
            <MdLabel className="text-xl text-customGrayDark" />
            <h3 className="text-lg font-semibold text-customGrayDark">
              Shipping Labels
            </h3>
          </div>

          {data?.selectedDeliveryType === "postal" && (
            <div onClick={(e) => e.stopPropagation()} className="flex gap-2 ">
              {isShippingLabelExpired ? (
                <Button
                  onClick={() => setShowBookShippmentList(true)}
                  className="flex justify-center items-center h-8 bg-customGreenColor hover:bg-customGreenColor/90 text-white px-4 py-2 rounded-md text-sm shadow-sm transition-all"
                >
                  <div className="flex items-center gap-2">
                    <BiSolidLabel className="text-base" />
                    Refresh Labels
                  </div>
                </Button>
              ) : (
                <Button
                  onClick={() => setShowScheduleDelivery(true)}
                  className="flex justify-center items-center h-8 bg-customGreenColor hover:bg-customGreenColor/90 text-white px-4 py-2 rounded-md text-sm shadow-sm transition-all"
                >
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="text-base" />
                    Schedule Pickup
                  </div>
                </Button>
              )}
              <ScheduleDeliverySection
                externalDeliveryId={data?.externalDeliveryId}
                orderDate={data?.createdAt}
                orderId={data?.id}
                isDialogOpen={showScheduleDelivery}
                setIsDialogOpen={setShowScheduleDelivery}
                deliveryDate={data?.scheduleDropoffDateTime}
                orderApi={orderApi}
                vendorEmail={data?.vendorEmail}
                setIsShippingLabelExpired={setIsShippingLabelExpired}
              />
              <BookShipmentSection
                isDialogOpen={showBookShippmentList}
                setIsDialogOpen={setShowBookShippmentList}
                orderId={data?.id}
                orderApi={orderApi}
                getClickShipRates={getClickShipRates}
                isLoading={isLoading}
                orderItems={data?.orderItems}
                setIsShippingLabelExpired={setIsShippingLabelExpired}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center"
        >
          <IoIosArrowDown
            className={cn(
              "text-xl text-customGrayDark transition-transform duration-300",
              isCollapsed ? "rotate-0" : "rotate-180"
            )}
          />
        </button>
      </div>

      <div
        className={cn(
          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
          "transition-all duration-300 ease-in-out origin-top",
          isCollapsed
            ? "animate-slideUp h-0 opacity-0 overflow-hidden"
            : "animate-slideDown opacity-100"
        )}
      >
        {data?.selectedDeliveryType === "postal" && isShippingLabelExpired ? (
          <div className="mt-2 p-3 rounded-md bg-red-50 text-red-600 col-span-full">
            <p className="text-sm font-medium flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              The shipping labels have expired and can no longer be used. Please
              refresh the labels before scheduling the shipment.
            </p>
          </div>
        ) : (
          parsedLabel.map((label, index) => (
            <div
              key={index}
              className="p-4 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">
                    Size: {label.size.toUpperCase()}
                  </span>
                  <span className="text-sm font-medium text-gray-600 flex items-center gap-1">
                    {label.format === "pdf" ? (
                      <FaRegFilePdf className="text-base" />
                    ) : (
                      <FaBarcode className="text-base" />
                    )}
                    Format: {label.format.toUpperCase()}
                  </span>
                </div>
                <Button
                  onClick={() => handleOpenLabel(label.url)}
                  className="mt-2 w-full bg-customGreenColor hover:bg-customGreenColor/90 text-white flex items-center justify-center gap-2"
                >
                  <FaDownload className="text-base" />
                  Open {label.format.toUpperCase()} Label
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrderLabeldata;
